import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../services/apiClient';
import '../../styles/fonts.css';
import './AccountActivation.css';

const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { key: 'upper', label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'lower', label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { key: 'digit', label: 'One digit', test: (pw) => /\d/.test(pw) },
  { key: 'special', label: 'One special character', test: (pw) => /[!@#$%^&*()_+\-=[\]{}|;':",./\<>?]/.test(pw) },
];

const AccountActivation = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);

  const [email, setEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fatalError, setFatalError] = useState('');
  const [formError, setFormError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const validateLink = async () => {
      if (!token) {
        setFatalError('Activation link is missing. Please contact info@eclipseer.com for a new link.');
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.validateAccountActivationLink(token);
        setEmail(response.data?.email || '');
        setExpiresAt(response.data?.expires_at || null);
      } catch (err) {
        const msg = err.response?.data?.error || 'Invalid activation link. Please contact info@eclipseer.com.';
        setFatalError(msg);
      } finally {
        setLoading(false);
      }
    };

    validateLink();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setValidationErrors([]);
    setSuccess('');

    if (newPassword.length < 8) {
      setFormError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authAPI.completeAccountActivation({
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      setSuccess(response.data?.message || 'Password set successfully. You can now sign in.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const data = err.response?.data || {};

      if (data.validation_errors && Array.isArray(data.validation_errors)) {
        setValidationErrors(data.validation_errors);
        setFormError(data.error || 'Password does not meet security requirements.');
      } else {
        const msg = data.error || 'Could not activate account. Please request a new link at info@eclipseer.com.';

        if (
          msg.toLowerCase().includes('expired') ||
          msg.toLowerCase().includes('invalid activation') ||
          msg.toLowerCase().includes('already been used')
        ) {
          setFatalError(msg);
        } else {
          setFormError(msg);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const rulesMet = PASSWORD_RULES.map((rule) => ({
    ...rule,
    met: rule.test(newPassword),
  }));

  const showForm = !fatalError && !success;

  return (
    <div className="activation-page">
      <div className="activation-panel">
        <div className="activation-header">
          <h1>Set Your Password</h1>
          {email ? <p>Welcome, <strong>{email}</strong></p> : null}
        </div>

        <div className="activation-divider" />

        {loading ? (
          <p className="activation-message">Validating activation link...</p>
        ) : (
          <>
            {fatalError && (
              <div className="activation-alert activation-alert--error">{fatalError}</div>
            )}

            {success && (
              <>
                <div className="activation-alert activation-alert--success">{success}</div>
                <p className="activation-footnote">
                  <Link to="/login">Go to Sign In →</Link>
                </p>
              </>
            )}

            {showForm && (
              <>
                {formError && (
                  <div className="activation-alert activation-alert--error">
                    {formError}
                    {validationErrors.length > 0 && (
                      <ul className="activation-validation-errors">
                        {validationErrors.map((ve, idx) => (
                          <li key={idx}>{ve}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="activation-form">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Create a strong password"
                    autoComplete="new-password"
                    required
                  />

                  {newPassword.length > 0 && (
                    <ul className="password-requirements">
                      {rulesMet.map((rule) => (
                        <li key={rule.key} className={rule.met ? 'met' : ''}>
                          <span className={`req-icon ${rule.met ? 'met' : ''}`}>
                            {rule.met ? '✓' : ''}
                          </span>
                          {rule.label}
                        </li>
                      ))}
                    </ul>
                  )}

                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    required
                  />

                  <button type="submit" disabled={submitting}>
                    {submitting ? 'Setting password...' : 'Set Password'}
                  </button>
                </form>

                {expiresAt && (
                  <p className="activation-footnote">
                    This link expires on {new Date(expiresAt).toLocaleString()}.
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccountActivation;
